import React from "react";

import { revisionKindLabel } from "@/components/shared/governance-labels";
import { Badge, Heading, ListRow, Text, Time } from "@/components/ui";
import type { ProgrammeGovernanceSummary } from "@/platform";

export interface CurriculumHistoryProps {
  governance: ProgrammeGovernanceSummary;
}

export function CurriculumHistory({
  governance,
}: CurriculumHistoryProps): React.ReactElement | null {
  if (governance.curriculumRevisions.length === 0) {
    return null;
  }

  return (
    <section className="mt-4" data-testid="curriculum-revision-history">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Heading level={3} size="md">
            Approved curriculum history
          </Heading>
          <Text tone="muted" className="mt-1">
            Revision numbers are given to approved curriculum only. Routine saves do not get one.
          </Text>
        </div>
        <Badge variant="outline">{governance.curriculumRevisions.length} approved</Badge>
      </div>
      <ol className="mt-3 space-y-2">
        {[...governance.curriculumRevisions].reverse().map((revision) => {
          const applicability = governance.revisionApplicability.find(
            (rule) => rule.governanceRevisionId === revision.id,
          );
          const lifecycle = governance.revisionLifecycle.find(
            (entry) => entry.governanceRevisionId === revision.id,
          );
          const lifecycleLabel = lifecycle?.status
            .split("-")
            .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
            .join(" ");
          return (
            <li key={revision.id}>
              <ListRow data-testid={`curriculum-revision-${revision.major}-${revision.minor}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone="primary">
                    {revision.major}.{revision.minor}
                  </Badge>
                  <span className="font-medium">{revision.institutionalLabel}</span>
                  <Badge size="sm" variant="outline">
                    {revisionKindLabel(revision.kind)}
                  </Badge>
                  {lifecycleLabel ? (
                    <Badge
                      size="sm"
                      tone={lifecycle?.status === "current" ? "success" : "neutral"}
                      data-testid={`revision-lifecycle-${lifecycle?.status}`}
                    >
                      {lifecycleLabel}
                    </Badge>
                  ) : null}
                </div>
                <Text
                  as="div"
                  size="sm"
                  tone="muted"
                  className="mt-2 flex flex-wrap gap-x-4 gap-y-1"
                >
                  <span>
                    Approved <Time value={revision.approvedAt} variant="date" />
                  </span>
                  {applicability ? <span>Effective {applicability.effectiveFrom}</span> : null}
                  {revision.nextRevalidationDue ? (
                    <span>Revalidation due {revision.nextRevalidationDue}</span>
                  ) : null}
                </Text>
              </ListRow>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
